import DOMPurify, { type Config } from 'dompurify';

import {
  filterIframeAllow,
  IFRAME_FORBIDDEN_ATTRS,
  IFRAME_REFERRER_POLICY,
  IFRAME_SANDBOX,
  IFRAME_SANDBOX_WITH_FORMS,
  iframeAllowsForms,
} from './iframe';

/**
 *
 * @example
 * input: <img src=x onerror=alert(1)//>
 * output: <img src="x">
 * @param textHtml string html content
 * @param config object of DOMPurify library configs. see https://github.com/cure53/DOMPurify#can-i-configure-dompurify
 * @return string sanitized html content
 */

// TODO: here we can set some default Tags and Attributes based on our needs later. e.g: DOMPurify.setConfig({ALLOWED_TAGS: ['iframe', '#text'], ALLOWED_ATTR: ['style']});

DOMPurify.addHook('uponSanitizeElement', function (currentNode, { tagName }) {
  if (tagName === 'iframe' && currentNode instanceof HTMLElement) {
    currentNode.textContent = null;
    if (currentNode.hasAttribute('allowfullscreen')) {
      // DOMPurify omits allowfullscreen="“”" from the output
      // but this works around that...
      currentNode.setAttribute('allowfullscreen', '');
    }
  }
});

// The iframe policy is applied *after* attribute filtering, not in
// uponSanitizeElement. Set any earlier, it is subject to the caller's
// ALLOWED_ATTR — so a caller that permits `iframe` without also permitting
// `sandbox` would have the policy stripped straight back off and get an
// unconstrained frame. Forced here, it holds for every config.
DOMPurify.addHook('afterSanitizeAttributes', function (currentNode) {
  if (currentNode.nodeName !== 'IFRAME') return;

  // Per-host, mirroring HtmlCleaner#force_iframe_sandbox: Office Online renders an
  // empty box without `allow-forms`. Read from the attribute DOMPurify has already
  // filtered, so the host granted forms is the one the browser will load.
  currentNode.setAttribute(
    'sandbox',
    iframeAllowsForms(currentNode.getAttribute('src') ?? undefined)
      ? IFRAME_SANDBOX_WITH_FORMS
      : IFRAME_SANDBOX,
  );
  currentNode.setAttribute('referrerpolicy', IFRAME_REFERRER_POLICY);
  // DOMPurify's own URI filter covers `src` whatever the caller allows, but
  // nothing covers `srcdoc` — a caller adding it to ADD_ATTR would otherwise
  // get a frame executing as our origin.
  IFRAME_FORBIDDEN_ATTRS.forEach((attr) => currentNode.removeAttribute(attr));

  // Sandbox does not constrain Permissions-Policy delegation, so `allow` needs
  // filtering too. Whatever reaches this point already passed the caller's
  // allowlist; if they did not permit `allow`, it is gone and there is nothing
  // to do — dropping it is the safe outcome either way.
  const allow = currentNode.getAttribute('allow');
  if (allow) currentNode.setAttribute('allow', filterIframeAllow(allow));
});

export default function sanitize(textHtml: string, config?: Config): string {
  if (config) return DOMPurify.sanitize(textHtml, config) as string;
  return DOMPurify.sanitize(textHtml);
}
